<script lang="ts">
  import { fly } from "svelte/transition";
  import { backOut } from "svelte/easing";
  import { router } from "../lib/router.svelte.js";
  import { library } from "../lib/stores/library.svelte.js";
  import { live } from "../lib/stores/live.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Cover from "./Cover.svelte";
  import Icon from "./Icon.svelte";

  /**
   * Leiste über der Navigation:
   *  – spielt ein anderes Gerät (z. B. das iPad am Flügel): Fernbedienung
   *  – sonst: die aktuelle Setlist mit „Starten“
   */
  const remote = $derived(live.others[0] ?? null);
  const remoteList = $derived(remote?.playlistId ? (playlists.byId.get(remote.playlistId) ?? null) : null);
  const remotePiece = $derived(remote?.documentId ? (library.byId.get(remote.documentId) ?? null) : null);
  const active = $derived(playlists.active);
  const activeIndex = $derived(active ? Math.max(0, active.currentIndex) : 0);
  const activePiece = $derived(
    active?.items[activeIndex] ? library.byId.get(active.items[activeIndex]!) : null,
  );

  function remoteStep(delta: number): void {
    if (!remote || !remoteList || remote.index === null) return;
    const index = remote.index + delta;
    if (index < 0 || index >= remoteList.items.length) return;
    void playlists.setCurrent(remoteList.id, index);
  }

  function startActive(): void {
    if (!active || !activePiece) return;
    router.go(`/noten/${activePiece.id}?setlist=${active.id}&i=${activeIndex}`);
  }
</script>

{#if remote && remotePiece}
  <div class="bar remote" class:wide={ui.wide} in:fly={{ y: 40, duration: 500, easing: backOut }}>
    <button type="button" class="who" onclick={() => remoteList && router.go(`/setlists/${remoteList.id}`)}>
      <span class="mini"><Cover src={remotePiece.thumb} ratio="1" radius="6px" /></span>
      <span class="text">
        <span class="title ellipsis">{remotePiece.name}</span>
        <span class="meta live">
          <span class="dot"></span>
          Auf dem {remote.device}{remoteList && remote.index !== null
            ? ` · Stück ${remote.index + 1} von ${remoteList.items.length}`
            : ""}
        </span>
      </span>
    </button>
    {#if remoteList && remote.index !== null}
      <div class="controls">
        <button
          type="button"
          class="icon-btn"
          aria-label="Vorheriges Stück auf dem {remote.device}"
          disabled={remote.index <= 0}
          onclick={() => remoteStep(-1)}
        >
          <Icon name="prev" />
        </button>
        <button
          type="button"
          class="icon-btn"
          aria-label="Nächstes Stück auf dem {remote.device}"
          disabled={remote.index >= remoteList.items.length - 1}
          onclick={() => remoteStep(1)}
        >
          <Icon name="next" />
        </button>
      </div>
    {/if}
  </div>
{:else if active && activePiece && ui.wide}
  <div class="bar" class:wide={ui.wide} in:fly={{ y: 40, duration: 500, easing: backOut }}>
    <button type="button" class="who" onclick={() => router.go(`/setlists/${active.id}`)}>
      <span class="mini"><Cover src={activePiece.thumb} ratio="1" radius="6px" /></span>
      <span class="text">
        <span class="title ellipsis">{active.name}</span>
        <span class="meta t3">Aktuelle Setlist · als Nächstes {activePiece.name}</span>
      </span>
    </button>
    <button type="button" class="btn btn-primary" onclick={startActive}
      ><Icon name="play" size={18} /> Starten</button
    >
  </div>
{/if}

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 8px 8px;
    padding: 8px 10px 8px 8px;
    border-radius: var(--r-lg);
    background: #1a1a1f;
    box-shadow: inset 0 0 0 1px var(--line);
    z-index: var(--z-nowbar);
  }
  .bar.remote {
    background: #142a5c;
  }
  .bar.wide {
    padding: 10px 14px 10px 10px;
  }
  .who {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    text-align: left;
  }
  .mini {
    width: 46px;
    flex: 0 0 46px;
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .title {
    font-weight: 800;
    font-size: 15px;
  }
  .meta {
    font-size: 12px;
    font-weight: 700;
  }
  .live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--accent-3);
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent-3);
    animation: pulse 1.8s ease-in-out infinite;
  }
  .controls {
    display: flex;
    gap: 2px;
  }
  .controls .icon-btn {
    color: var(--text);
  }
  @keyframes pulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(61, 220, 151, 0.6);
    }
    60% {
      box-shadow: 0 0 0 8px rgba(61, 220, 151, 0);
    }
  }
</style>
